import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { RelocationStatus } from "@/gql/graphql";
import { ArchiveBoxIcon, PencilIcon } from "@heroicons/react/24/outline";
import { CopyIcon, Megaphone, Star } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowTopRightOnSquareIcon, PlayIcon } from "@heroicons/react/20/solid";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { GapRentalEditDialog } from "@/app/GapRentals/Components/GapRentalEditDialog";
import { toast } from "sonner";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  duplicateRelocationMutation,
  setRelocationPriorityMutation,
  updateRelocationStatuses,
} from "@/app/Relocations/GraphQL/relocationMutations";
import { PromoteRelocationDialog } from "@/app/Promotions/Components/PromoteRelocationDialog";
import { RelocationListItem } from "@/app/Relocations/GraphQL/relocationListQuery";
import { RelocationRecord } from "@/app/Relocations/GraphQL/relocationRecordQuery";
import { useTranslations } from "use-intl";

/**
 * Gap rentals share the relocation mutations but not its route-shaped actions:
 * there are no extra destinations to add and no pro driver to move a vehicle
 * that never leaves its office.
 */
export function useGapRentalActions(): ActionsHook<
  RelocationListItem | RelocationRecord
> {
  const { mutateAsync } = useGqlMutation(updateRelocationStatuses);
  const { mutateAsync: duplicate } = useGqlMutation(
    duplicateRelocationMutation,
  );
  const { mutateAsync: setPriority } = useGqlMutation(
    setRelocationPriorityMutation,
  );
  const { open: openPromoteDialog } = useDialog(PromoteRelocationDialog);
  const navigate = useNavigate();
  const { open } = useDialog(GapRentalEditDialog);
  const t = useTranslations("relocation.actions");

  return (gapRental) => {
    return [
      {
        label: t("label"),
        items: [
          {
            actions: [
              {
                label: t("ready"),
                Icon: PlayIcon,
                color: "green",
                isHidden: gapRental.status !== RelocationStatus.Draft,
                handleAction: () => {
                  return mutateAsync({
                    ids: [gapRental.id],
                    status: RelocationStatus.Ready,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("view"),
                Icon: ArrowTopRightOnSquareIcon,
                handleAction: async () => {
                  // customer_url is the canonical deal URL, built backend-side
                  // so admin can't drift from the marketplace route shape.
                  // preview=true so non-READY deals (archived/expired/etc.)
                  // render instead of 301-ing to the corridor listing.
                  const url = new URL(gapRental.customer_url);
                  url.searchParams.set("preview", "true");
                  window.open(url, "_blank");
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("edit"),
                isHiddenTopBar: true,
                Icon: PencilIcon,
                handleAction: () => {
                  open({
                    id: gapRental.id,
                  });
                },
              },
            ],
          },
          {
            actions: [
              {
                label: t("duplicate"),
                Icon: CopyIcon,
                handleAction: async () => {
                  const res = await duplicate({
                    id: gapRental.id,
                  });

                  toast.success(t("duplicatedSuccess"), {
                    action: {
                      label: t("view"),
                      onClick: () => {
                        navigate({
                          from: "/$tenant/$orgId/supplier/{-$supplierId}",
                          to: "/$tenant/$orgId/supplier/{-$supplierId}/gap-rentals/$relocationId",
                          params: (prev) => ({
                            ...prev,
                            relocationId: res.duplicateRelocation.id,
                          }),
                        });
                      },
                    },
                  });
                },
              },
              {
                label: t("setPriority"),
                Icon: Star,
                isAdminAction: true,
                isHidden:
                  gapRental.priority !== null &&
                  gapRental.priority !== undefined,
                handleAction: async () => {
                  await setPriority({
                    id: gapRental.id,
                    priority: 0,
                  });

                  toast.success(t("prioritySetSuccess"));
                },
              },
              {
                label: t("promote"),
                Icon: Megaphone,
                isAdminAction: true,
                handleAction: () => {
                  openPromoteDialog({ relocationId: gapRental.id });
                },
              },
              {
                label: t("removePriority"),
                Icon: Star,
                isAdminAction: true,
                isHidden:
                  gapRental.priority === null ||
                  gapRental.priority === undefined,
                handleAction: async () => {
                  await setPriority({
                    id: gapRental.id,
                    priority: null,
                  });

                  toast.success(t("priorityRemovedSuccess"));
                },
              },
            ],
          },
          {
            actions: [
              {
                isHidden: gapRental.status !== RelocationStatus.Ready,
                label: t("archive"),
                color: "purple",
                Icon: ArchiveBoxIcon,
                handleAction: async () => {
                  await mutateAsync({
                    ids: [gapRental.id],
                    status: RelocationStatus.Archived,
                  });
                },
              },
            ],
          },
        ],
      },
    ];
  };
}
